import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  captureExternalAppEvidence,
  completeExternalAppCalibration,
  confirmExternalAppTest,
  createExternalAppContentTarget,
  executeExternalAppContentTarget,
  getExternalAppEvidenceScreenshot,
  listExternalAppContentTargets,
  recordExternalAppBaseline,
  resetExternalAppCalibrationAction,
  saveExternalAppProfile,
  saveExternalAppCalibrationStartPoint,
  sendExternalAppCalibrationAction,
  startExternalAppCalibration,
  testExternalAppCalibrationStartPoint,
  testExternalAppAction,
  updateExternalAppContentTarget,
  updateExternalAppCalibrationStartPoint,
} from './externalAppControlApi';
import PromptDetectionPanel from './PromptDetectionPanel';
import PlaybackHealthPanel from './PlaybackHealthPanel';
import LaunchRecipePanel from './LaunchRecipePanel';
import { normalizedPointFromImage } from './visualTemplateSelection';
import {
  buildNavigationDestinationTarget,
  findNavigationDestinationTarget,
} from './navigationDestinationTargets';

const ACTION_LABELS = {
  home: 'Home',
  back: 'Back',
  up: 'Up',
  down: 'Down',
  left: 'Left',
  right: 'Right',
  enter: 'OK / Select',
  play_pause: 'Play / Pause',
  play: 'Play',
  pause: 'Pause',
  stop: 'Stop',
  next: 'Next',
  previous: 'Previous',
  fast_forward: 'Fast forward',
  rewind: 'Rewind',
  seek_forward: 'Seek forward',
  seek_back: 'Seek back',
};

const ACTION_PROMPTS = {
  up: 'Did the highlight move up?',
  down: 'Did the highlight move down?',
  left: 'Did the highlight move left?',
  right: 'Did the highlight move right?',
  enter: 'Did OK select the highlighted item?',
  back: 'Did the app go back one screen?',
  home: 'Did the expected home screen appear?',
  play_pause: 'Did playback change between playing and paused?',
  play: 'Did playback start?',
  pause: 'Did playback pause?',
  stop: 'Did playback stop?',
};

const normalizeSuggestedActions = (adapter) => {
  const actions = Array.isArray(adapter?.suggestedCalibrationTests)
    ? adapter.suggestedCalibrationTests
    : [];
  return Array.from(new Set(actions))
    .filter((action) => action !== 'launch')
    .map((action) => {
      if (action === 'seek_forward') return 'fast_forward';
      if (action === 'seek_back') return 'rewind';
      return action;
    });
};

const isSuitestreamEntry = (entry) => {
  const packageName = String(entry?.package_name || '').toLowerCase();
  const appName = String(entry?.app_name || '').toLowerCase();
  return packageName.includes('suitestream') || appName.includes('suitestream');
};

function AppTrainingConfigurator({
  app,
  adapter,
  deviceId,
  profile,
  availableApps = [],
  onClose,
  onColdLaunch,
  onLaunchApp,
  onProfileUpdated,
}) {
  const label = app?.app_name || app?.package_name || 'Unknown App';
  const packageName = app?.package_name || '';
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [launchVerified, setLaunchVerified] = useState(false);
  const [latestEvidence, setLatestEvidence] = useState(null);
  const [latestScreenshot, setLatestScreenshot] = useState('');
  const [pendingTest, setPendingTest] = useState(null);
  const [completedTests, setCompletedTests] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [includeScreenshots, setIncludeScreenshots] = useState(false);
  const [pathRecording, setPathRecording] = useState(null);
  const [savedStartPoints, setSavedStartPoints] = useState([]);
  const [searchTestText, setSearchTestText] = useState('BBC News');
  const [channelTargets, setChannelTargets] = useState([]);
  const [destinationName, setDestinationName] = useState('');
  const [channelForm, setChannelForm] = useState({
    name: '',
    channelNumber: '',
    currentPosition: '1',
    targetPosition: '1',
    totalChannels: '',
    wraps: false,
  });
  const [visualSelection, setVisualSelection] = useState(null);
  const [visualThreshold, setVisualThreshold] = useState(0.82);
  const [startPointEditor, setStartPointEditor] = useState(null);
  const baselineSectionRef = useRef(null);
  const didAutoStartRef = useRef(false);
  const visualImageRef = useRef(null);
  const visualDragStartRef = useRef(null);

  const actions = useMemo(() => normalizeSuggestedActions(adapter), [adapter]);
  const passedCount = completedTests.filter((test) => test.outcome === 'passed').length;
  const workingActions = useMemo(
    () => completedTests
      .filter((test) => test.outcome === 'passed')
      .map((test) => test.action),
    [completedTests]
  );
  const launchSourceApps = useMemo(
    () => availableApps.filter((entry) => (
      entry?.package_name
      && entry.package_name !== packageName
      && !isSuitestreamEntry(entry)
    )),
    [availableApps, packageName]
  );

  const run = useCallback(async (key, operation) => {
    setBusy(key);
    setMessage('');
    setError('');
    try {
      return await operation();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Setup failed');
      return null;
    } finally {
      setBusy('');
    }
  }, []);

  const ensureProfile = useCallback(async () => {
    if (profile) return profile;
    return saveExternalAppProfile(deviceId, packageName, {
      displayName: label,
      adapterKey: adapter?.key || 'generic',
      adapterVersion: adapter?.version || 1,
      readiness: 'needs_setup',
      preferredLaunchMethod: 'app',
      preferredNavigationMethod: 'app',
      controlPolicy: {},
      capabilities: {},
      profile: {
        adapterMatched: adapter?.matched === true,
      },
    });
  }, [adapter, deviceId, label, packageName, profile]);

  const hydrateSession = useCallback((nextSession) => {
    const restoredTests = Array.isArray(nextSession?.testResults)
      ? [...nextSession.testResults]
      : [];
    Object.entries(nextSession?.draftCapabilities?.actions || {}).forEach(([action, capability]) => {
      if (capability?.calibrated && !restoredTests.some((entry) => entry.action === action)) {
        restoredTests.push({
          id: `restored-${action}`,
          action,
          outcome: 'passed',
          confidence: 'saved',
          confirmedByUser: true,
        });
      }
    });
    const startPoints = Object.values(nextSession?.draftProfile?.startPoints || {});
    const interruptions = Array.isArray(nextSession?.draftProfile?.interruptionHandlers)
      ? nextSession.draftProfile.interruptionHandlers
      : [];
    const restoredBaseline = nextSession?.baseline
      || nextSession?.draftProfile?.baselines?.playback
      || null;
    setSession(nextSession);
    setCompletedTests(restoredTests);
    setSavedStartPoints([...startPoints, ...interruptions]);
    setBaseline(restoredBaseline);
    setLaunchVerified(Boolean(restoredTests.length || startPoints.length || restoredBaseline));
  }, []);

  const openSession = useCallback(async (automatic = false) => {
    const result = await run('start', async () => {
      const nextProfile = await ensureProfile();
      if (!nextProfile?.id) throw new Error('Could not create the app profile.');
      if (!profile) onProfileUpdated(nextProfile);
      return startExternalAppCalibration(nextProfile.id);
    });
    if (result) {
      hydrateSession(result);
      setMessage(
        result.testResults?.length || Object.keys(result.draftCapabilities?.actions || {}).length
          ? 'Saved setup restored. Continue training or test the recorded navigation.'
          : automatic
            ? 'Training session ready. First validate that the app opens cleanly.'
            : 'Training session started. First validate that the app opens cleanly.'
      );
    }
  }, [ensureProfile, hydrateSession, onProfileUpdated, profile, run]);

  useEffect(() => {
    if (didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    openSession(true);
  }, [openSession]);

  useEffect(() => {
    if (!session?.profileId) return undefined;
    let cancelled = false;
    listExternalAppContentTargets(deviceId)
      .then((targets) => {
        if (!cancelled) {
          setChannelTargets(targets.filter((target) => target.profileId === session.profileId));
        }
      })
      .catch(() => {
        if (!cancelled) setError('Saved channel targets could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, session?.profileId]);

  const loadScreenshot = useCallback(async (evidenceId, force = false) => {
    if ((!includeScreenshots && !force) || !session?.id || !evidenceId) {
      setLatestScreenshot('');
      return;
    }
    const screenshot = await getExternalAppEvidenceScreenshot(session.id, evidenceId);
    setLatestScreenshot(screenshot?.dataUrl || '');
  }, [includeScreenshots, session?.id]);

  const handleLaunchValidation = async () => {
    const evidence = await run('launch', async () => {
      await onColdLaunch(packageName);
      return captureExternalAppEvidence(session.id, includeScreenshots, 6000);
    });
    if (evidence) {
      setLaunchVerified(true);
      setLatestEvidence(evidence);
      setMessage(`Launch verified: ${evidence.foregroundPackageName} is in the foreground.`);
      await run('screenshot', () => loadScreenshot(evidence.evidenceId));
    }
  };

  const handleTest = async (action) => {
    const test = await run(`test:${action}`, () =>
      testExternalAppAction(session.id, action, includeScreenshots)
    );
    if (!test) return;
    setPendingTest(test);
    if (test.outcome === 'passed' && test.confidence === 'high') {
      setCompletedTests((current) => [...current.filter((entry) => entry.action !== action), test]);
      setPendingTest(null);
      setMessage(`${ACTION_LABELS[action] || action} was accepted from strong accessibility evidence.`);
    } else {
      setMessage(test.reason === 'no_observable_state_change'
        ? 'The accessibility snapshot could not prove the result. Please confirm what happened on the TV.'
        : 'Please confirm the result shown on the TV.');
    }
    await run('screenshot', () => loadScreenshot(test.afterEvidenceId));
  };

  const handleConfirm = async (outcome) => {
    const confirmed = await run('confirm', () =>
      confirmExternalAppTest(session.id, pendingTest.id, outcome)
    );
    if (confirmed) {
      setCompletedTests((current) => [
        ...current.filter((entry) => entry.action !== confirmed.action),
        confirmed,
      ]);
      setPendingTest(null);
      setMessage(
        outcome === 'passed'
          ? `${ACTION_LABELS[confirmed.action] || confirmed.action} saved as working.`
          : `${ACTION_LABELS[confirmed.action] || confirmed.action} marked as not working.`
      );
    }
  };

  const handleNavigate = async (action) => {
    const result = await run(`navigate:${action}`, () =>
      sendExternalAppCalibrationAction(session.id, action)
    );
    if (result) {
      if (pathRecording) {
        const now = Date.now();
        setPathRecording((current) => {
          if (!current) return current;
          const steps = current.steps.map((step, index) => (
            index === current.steps.length - 1
              ? {
                ...step,
                delayMs: Math.min(10000, Math.max(0, now - step.sentAt)),
              }
              : step
          ));
          return { ...current, steps: [...steps, { action, delayMs: 500, sentAt: now }] };
        });
        setMessage(`${ACTION_LABELS[action] || action} sent and recorded.`);
      } else {
        setMessage(`${ACTION_LABELS[action] || action} sent. Use Retest if it no longer works.`);
      }
    }
  };

  const handleResetAction = async (action) => {
    const result = await run(`reset:${action}`, () =>
      resetExternalAppCalibrationAction(session.id, action)
    );
    if (result) {
      setCompletedTests((current) => current.filter((entry) => entry.action !== action));
      if (pendingTest?.action === action) setPendingTest(null);
      setMessage(`${ACTION_LABELS[action] || action} reset. The next press will test it again.`);
    }
  };

  const publishNavigationDestination = async (destination, anchorOverride = null) => {
    const anchor = anchorOverride
      || savedStartPoints.find((entry) => entry.kind === 'anchor');
    if (!anchor) throw new Error('Record Known Home before publishing a destination.');
    const payload = buildNavigationDestinationTarget({
      deviceId,
      profileId: session.profileId,
      anchor,
      destination,
    });
    let existing = findNavigationDestinationTarget(channelTargets, destination.id);
    if (!existing) {
      const latestTargets = (await listExternalAppContentTargets(deviceId))
        .filter((target) => target.profileId === session.profileId);
      setChannelTargets(latestTargets);
      existing = findNavigationDestinationTarget(latestTargets, destination.id);
    }
    const target = existing
      ? await updateExternalAppContentTarget(existing.id, payload)
      : await createExternalAppContentTarget(payload);
    setChannelTargets((current) => [
      ...current.filter((entry) => entry.id !== target.id),
      target,
    ]);
    return target;
  };

  const handleStartPathRecording = async (kind, visualMode = false, customLabel = '') => {
    const startEvidence = await run(`record:${kind}`, async () => {
      if (kind === 'anchor') {
        await onLaunchApp(packageName);
      }
      return captureExternalAppEvidence(
        session.id,
        visualMode || includeScreenshots,
        kind === 'anchor' ? 10000 : 0
      );
    });
    if (!startEvidence) return;
    setLaunchVerified(true);
    setLatestEvidence(startEvidence);
    if (visualMode) {
      await run('screenshot', () => loadScreenshot(startEvidence.evidenceId, true));
      setVisualSelection(null);
    }
    setPathRecording({
      kind,
      label: kind === 'anchor'
        ? 'Known home'
        : kind === 'channel_list'
          ? 'Channel list reference'
        : kind === 'playback'
          ? 'First playback'
        : kind === 'search'
          ? 'Search'
          : kind === 'destination'
            ? customLabel.trim()
          : 'One-time interruption',
      runPolicy: kind === 'interruption' ? 'once_per_install' : 'on_demand',
      visualMode,
      startEvidence,
      startedAt: Date.now(),
      steps: [],
    });
    setMessage(
      kind === 'interruption'
        ? 'Current interruption captured. Use working controls to dismiss it, then save the recording.'
        : kind === 'anchor'
          ? 'App launched. Use working controls to reach a repeatable home anchor, such as the top-left Search icon.'
          : kind === 'channel_list'
            ? 'Starting from Known Home, open the channel list and place focus on a repeatable reference channel.'
          : kind === 'destination'
            ? `Starting from Known Home, navigate to ${customLabel.trim()}.`
          : `Starting from the known anchor, navigate to ${kind === 'search' ? 'the search field' : 'the first channel playing'}.`
    );
  };

  const handleStartDestinationRecording = () => {
    const name = destinationName.trim();
    if (!name) {
      setError('Name this destination before recording it.');
      return;
    }
    handleStartPathRecording('destination', false, name);
  };

  const handleCancelPathRecording = () => {
    setPathRecording(null);
    setVisualSelection(null);
    setMessage('Navigation recording discarded.');
  };

  const handleSavePathRecording = async () => {
    if (!pathRecording?.steps.length) {
      setError('Record at least one navigation press before saving.');
      return;
    }
    if (pathRecording.visualMode && !visualSelection) {
      setError('Draw a rectangle around the distinctive prompt or button before saving.');
      return;
    }
    const result = await run('save-path', async () => {
      let visualTemplate = null;
      if (pathRecording.visualMode) {
        const image = visualImageRef.current;
        if (!image?.naturalWidth || !image?.naturalHeight || !latestScreenshot) {
          throw new Error('The visual reference screenshot is unavailable.');
        }
        const canvas = document.createElement('canvas');
        const sourceX = Math.round(visualSelection.x * image.naturalWidth);
        const sourceY = Math.round(visualSelection.y * image.naturalHeight);
        const sourceWidth = Math.max(1, Math.round(visualSelection.width * image.naturalWidth));
        const sourceHeight = Math.max(1, Math.round(visualSelection.height * image.naturalHeight));
        const scale = Math.min(1, 320 / sourceWidth, 180 / sourceHeight);
        canvas.width = Math.max(1, Math.round(sourceWidth * scale));
        canvas.height = Math.max(1, Math.round(sourceHeight * scale));
        canvas.getContext('2d').drawImage(
          image,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          canvas.width,
          canvas.height
        );
        visualTemplate = {
          region: visualSelection,
          threshold: visualThreshold,
          templateDataUrl: canvas.toDataURL('image/jpeg', 0.82),
        };
      }
      const endEvidence = await captureExternalAppEvidence(
        session.id,
        includeScreenshots
      );
      const startPoint = await saveExternalAppCalibrationStartPoint(session.id, {
        kind: pathRecording.kind,
        label: pathRecording.label,
        runPolicy: pathRecording.runPolicy,
        initialDelayMs: pathRecording.steps[0]?.sentAt
          ? Math.min(30000, Math.max(0, pathRecording.steps[0].sentAt - pathRecording.startedAt))
          : 0,
        steps: pathRecording.steps.map(({ action, delayMs }) => ({ action, delayMs })),
        visualTemplate,
        startEvidenceId: pathRecording.startEvidence.evidenceId,
        endEvidenceId: endEvidence.evidenceId,
      });
      let recordedBaseline = null;
      if (pathRecording.kind === 'playback') {
        recordedBaseline = await recordExternalAppBaseline(session.id, includeScreenshots);
      }
      return { startPoint, endEvidence, recordedBaseline };
    });
    if (!result) return;
    setSavedStartPoints((current) => [
      ...current.filter((entry) => (
        result.startPoint.kind === 'destination'
          ? entry.id !== result.startPoint.id
          : entry.kind !== result.startPoint.kind || entry.kind === 'interruption'
      )),
      result.startPoint,
    ]);
    setLatestEvidence(result.endEvidence);
    if (result.recordedBaseline) setBaseline(result.recordedBaseline);
    setPathRecording(null);
    setVisualSelection(null);
    if (result.startPoint.kind === 'destination') {
      setDestinationName('');
      const target = await run(`publish-destination:${result.startPoint.id}`, () => (
        publishNavigationDestination(result.startPoint)
      ));
      setMessage(
        target
          ? `${result.startPoint.label} is saved and available as an app destination.`
          : `${result.startPoint.label} navigation was saved, but its destination button still needs publishing.`
      );
    } else {
      setMessage(`${result.startPoint.label} navigation saved with ${result.startPoint.steps.length} step(s).`);
    }
  };

  const visualPoint = (event) => {
    return normalizedPointFromImage(event.clientX, event.clientY, visualImageRef.current);
  };

  const handleVisualPointerDown = (event) => {
    const point = visualPoint(event);
    if (!point) return;
    visualDragStartRef.current = point;
    setVisualSelection({ ...point, width: 0, height: 0 });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleVisualPointerMove = (event) => {
    if (!visualDragStartRef.current) return;
    const start = visualDragStartRef.current;
    const point = visualPoint(event);
    if (!point) return;
    setVisualSelection({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x),
      height: Math.abs(point.y - start.y),
    });
  };

  const handleVisualPointerUp = () => {
    visualDragStartRef.current = null;
    setVisualSelection((current) => (
      current?.width >= 0.01 && current?.height >= 0.01 ? current : null
    ));
  };

  const handleTestStartPoint = async (startPoint) => {
    const result = await run(`test-path:${startPoint.id}`, async () => {
      if (startPoint.kind !== 'interruption') {
        await onColdLaunch(packageName);
        await captureExternalAppEvidence(session.id, false, 6000);
      }
      return testExternalAppCalibrationStartPoint(session.id, startPoint.id);
    });
    if (!result) return;
    setLatestEvidence(result);
    if (result.matched === false) {
      const confidence = result.visualMatch
        ? ` Visual similarity was ${Math.round(result.visualMatch.similarity * 100)}%`
        : '';
      setMessage(`The saved interruption was not detected, so no response was sent.${confidence}`);
      return;
    }
    setMessage(
      `${startPoint.label} test sent ${result.executedSteps.length} command(s) and finished on ${result.screenKey}.`
      + (result.visualMatch
        ? ` Visual similarity: ${Math.round(result.visualMatch.similarity * 100)}%.`
        : '')
    );
  };

  const handleOpenStartPointEditor = (startPoint) => {
    setStartPointEditor({
      source: startPoint,
      label: startPoint.label,
      initialDelayMs: Number(startPoint.initialDelayMs) || 0,
      steps: startPoint.steps.map((step) => ({
        action: step.action,
        delayMs: Number(step.delayMs) || 0,
      })),
      currentStepIndex: 0,
      capturedEvidence: null,
      screenshot: '',
    });
    setError('');
    setMessage('');
  };

  const handleUpdateEditorStep = (index, patch) => {
    setStartPointEditor((current) => ({
      ...current,
      steps: current.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, ...patch } : step
      ),
    }));
  };

  const handleSaveStartPointEditor = async () => {
    const updated = await run('edit-path:save', () =>
      updateExternalAppCalibrationStartPoint(
        session.id,
        startPointEditor.source.id,
        {
          label: startPointEditor.label,
          initialDelayMs: startPointEditor.initialDelayMs,
          steps: startPointEditor.steps,
        }
      )
    );
    if (!updated) return;
    setSavedStartPoints((current) =>
      current.map((entry) => entry.id === updated.id ? updated : entry)
    );
    setStartPointEditor((current) => ({ ...current, source: updated }));
    if (updated.kind === 'destination') {
      const target = await run(`publish-destination:${updated.id}`, () => (
        publishNavigationDestination(updated)
      ));
      setMessage(
        target
          ? `${updated.label} timings and destination button saved.`
          : `${updated.label} timings saved, but its destination button could not be updated.`
      );
    } else if (updated.kind === 'anchor') {
      const destinations = savedStartPoints.filter((entry) => entry.kind === 'destination');
      if (!destinations.length) {
        setMessage(`${updated.label} timings saved.`);
        return;
      }
      const synced = await run('publish-destinations:anchor', () => (
        Promise.all(destinations.map((destination) => (
          publishNavigationDestination(destination, updated)
        )))
      ));
      setMessage(
        synced
          ? `Known Home timings saved for ${destinations.length} named destination(s).`
          : 'Known Home timings saved; published destination fallbacks could not all be refreshed.'
      );
    } else {
      setMessage(`${updated.label} timings saved.`);
    }
  };

  const handleSendManualStep = async () => {
    const step = startPointEditor?.steps[startPointEditor.currentStepIndex];
    if (!step) return;
    const result = await run('edit-path:step', () =>
      sendExternalAppCalibrationAction(session.id, step.action)
    );
    if (!result) return;
    setStartPointEditor((current) => ({
      ...current,
      currentStepIndex: Math.min(current.steps.length, current.currentStepIndex + 1),
    }));
    setMessage(
      `${ACTION_LABELS[step.action] || step.action} sent. Inspect the TV before sending the next step.`
    );
  };

  const handleCaptureEditorScreen = async () => {
    const evidence = await run('edit-path:capture', () =>
      captureExternalAppEvidence(session.id, true, 3000)
    );
    if (!evidence) return;
    const screenshot = await run('edit-path:screenshot', () =>
      getExternalAppEvidenceScreenshot(session.id, evidence.evidenceId)
    );
    if (!screenshot) return;
    setStartPointEditor((current) => ({
      ...current,
      capturedEvidence: evidence,
      screenshot: screenshot.dataUrl || '',
    }));
    setMessage('Current screen captured. You can now train a response or continue stepping.');
  };

  const handleTrainCapturedInterruption = () => {
    if (!startPointEditor?.capturedEvidence || !startPointEditor.screenshot) return;
    const startEvidence = startPointEditor.capturedEvidence;
    setLatestEvidence(startEvidence);
    setLatestScreenshot(startPointEditor.screenshot);
    setVisualSelection(null);
    setPathRecording({
      kind: 'interruption',
      label: 'Recurring screen interruption',
      runPolicy: 'whenever_matched',
      visualMode: true,
      startEvidence,
      startedAt: Date.now(),
      steps: [],
    });
    setStartPointEditor(null);
    setMessage(
      'Interruption training started from the captured screen. Select a stable region, then dismiss it with the recording remote.'
    );
  };

  const handleCreateChannelTarget = async () => {
    const name = channelForm.name.trim();
    const anchor = savedStartPoints.find((entry) => entry.kind === 'anchor');
    const channelList = savedStartPoints.find((entry) => entry.kind === 'channel_list');
    if (!name) {
      setError('Enter a name for this favourite channel.');
      return;
    }
    if (!anchor) {
      setError('Record the Known Home path before creating a channel target.');
      return;
    }
    const copySegment = (entry) => ({
      initialDelayMs: entry?.initialDelayMs || 0,
      steps: (entry?.steps || []).map(({ action, delayMs }) => ({ action, delayMs })),
    });
    const strategies = [];
    const channelNumber = channelForm.channelNumber.replace(/\D/g, '').slice(0, 4);
    if (channelNumber) {
      strategies.push({
        id: 'direct-number',
        type: 'channel_number',
        launchDelayMs: 6000,
        channelNumber,
        anchor: copySegment(anchor),
        confirmWithEnter: true,
      });
    }
    const currentPosition = Number(channelForm.currentPosition);
    const targetPosition = Number(channelForm.targetPosition);
    const totalChannels = Number(channelForm.totalChannels);
    if (
      channelList &&
      Number.isInteger(currentPosition) &&
      Number.isInteger(targetPosition) &&
      Number.isInteger(totalChannels) &&
      totalChannels > 1
    ) {
      strategies.push({
        id: 'channel-list',
        type: 'channel_navigation',
        launchDelayMs: 6000,
        anchor: copySegment(anchor),
        channelList: copySegment(channelList),
        currentPosition,
        targetPosition,
        totalChannels,
        wraps: channelForm.wraps,
      });
    }
    if (!strategies.length) {
      setError('Add a direct channel number or record/configure a channel-list route.');
      return;
    }
    const target = await run('create-channel', () => createExternalAppContentTarget({
      deviceId,
      profileId: session.profileId,
      name,
      kind: 'channel',
      seed: {
        channelNumber: channelNumber || null,
        listPosition: Number.isInteger(targetPosition) ? targetPosition : null,
      },
      strategies,
      verification: {
        foregroundPackageRequired: true,
        expectedScreen: 'playback_active',
      },
    }));
    if (!target) return;
    setChannelTargets((current) => [...current, target]);
    setChannelForm((current) => ({ ...current, name: '', channelNumber: '' }));
    setMessage(
      `${target.name} saved with ${target.strategies.length} ${target.strategies.length === 1 ? 'strategy' : 'strategies'}.`
    );
  };

  const handleTestChannelTarget = async (target) => {
    const result = await run(`test-target:${target.id}`, () =>
      executeExternalAppContentTarget(target.id)
    );
    if (!result) return;
    setMessage(
      `${target.name} executed using ${result.result?.strategyId || 'its first verified strategy'}.`
    );
  };

  const handlePublishDestination = async (destination) => {
    const target = await run(`publish-destination:${destination.id}`, () => (
      publishNavigationDestination(destination)
    ));
    if (target) setMessage(`${destination.label} is available as an app destination.`);
  };

  const handleComplete = async () => {
    const result = await run('complete', () => completeExternalAppCalibration(session.id));
    if (!result) return;
    await onProfileUpdated();
    setSession(null);
    setMessage(`Setup saved. App readiness is now ${result.readiness.replace(/_/g, ' ')}.`);
  };

  const handleClose = () => onClose();

  return (
    <section className="apps-configurator apps-configurator--training" aria-label={`Set up ${label}`}>
      <div className="apps-configurator-header">
        <div>
          <span className="apps-setup-eyebrow">Guided app setup</span>
          <h3>{label}</h3>
          <p>
            {adapter?.matched
              ? `${adapter.label} is recognised. Its suggested tests still need confirmation on this device.`
              : 'This app is not recognised yet. We will learn only the controls you test.'}
          </p>
        </div>
        <button type="button" onClick={handleClose} disabled={Boolean(busy)}>
          Close
        </button>
      </div>

      <div className="apps-configurator-summary-grid">
        <div className="apps-configurator-summary-card">
          <span>Adapter</span>
          <strong>{adapter?.label || 'Generic Android App'}</strong>
        </div>
        <div className="apps-configurator-summary-card">
          <span>Current readiness</span>
          <strong>{(profile?.readiness || 'needs_setup').replace(/_/g, ' ')}</strong>
        </div>
        <div className="apps-configurator-summary-card">
          <span>Working controls</span>
          <strong>{passedCount}</strong>
        </div>
        <div className="apps-configurator-summary-card">
          <span>Playback baseline</span>
          <strong>{baseline ? 'Recorded' : 'Not recorded'}</strong>
        </div>
      </div>

      {!session ? (
        <div className="apps-configurator-section apps-setup-start">
          <h4>{profile?.readiness === 'ready' ? 'Retrain this app' : 'Start setup'}</h4>
          <p>
            Training changes stay in a resumable draft until you press Save Setup. Closing this panel keeps
            the draft; Save Setup publishes it as the working profile.
          </p>
          <button type="button" className="primary" onClick={() => openSession(false)} disabled={busy === 'start'}>
            {busy === 'start' ? 'Loading saved setup…' : profile?.readiness === 'ready' ? 'Continue Setup' : 'Start Guided Setup'}
          </button>
        </div>
      ) : (
        <>
          <div className="apps-setup-progress" aria-label="Setup progress">
            <span className={launchVerified ? 'complete' : 'active'}>1. Launch</span>
            <span className={passedCount ? 'complete' : launchVerified ? 'active' : ''}>2. Controls</span>
            <span className={baseline ? 'complete' : passedCount ? 'active' : ''}>3. Baseline</span>
            <span className={baseline || passedCount ? 'active' : ''}>4. Save</span>
          </div>

          <div className="apps-configurator-section">
            <h4>1. Clean launch check</h4>
            <p>
              Suitestream is opened first, then {label}. The server confirms the foreground package before
              training any buttons.
            </p>
            <button
              type="button"
              className="primary"
              onClick={handleLaunchValidation}
              disabled={Boolean(busy)}
            >
              {busy === 'launch' ? 'Launching and checking…' : launchVerified ? 'Run Launch Check Again' : 'Launch and Verify'}
            </button>
            {latestEvidence && (
              <div className="apps-setup-evidence">
                <strong>{latestEvidence.foregroundPackageName}</strong>
                <span>{latestEvidence.screenKey} · {latestEvidence.telemetryStatus}</span>
              </div>
            )}
          </div>

          <div className="apps-configurator-section">
            <h4>2. Test Remote controls</h4>
            <p>Watch the TV while each command runs. Confirm only controls that actually worked.</p>
            <div className="apps-setup-action-grid">
              {actions.map((action) => {
                const result = completedTests.find((entry) => entry.action === action);
                const isWorking = result?.outcome === 'passed';
                return (
                  <div key={action} className={`apps-setup-action-shell${isWorking ? ' working' : ''}`}>
                    <button
                      type="button"
                      className={isWorking ? 'apps-setup-action working' : 'apps-setup-action'}
                      onClick={() => (isWorking ? handleNavigate(action) : handleTest(action))}
                      disabled={!launchVerified || Boolean(busy) || Boolean(pendingTest)}
                    >
                      <strong>{ACTION_LABELS[action] || action.replace(/_/g, ' ')}</strong>
                      <span>{isWorking ? 'Working · press to navigate' : result ? 'Not working' : 'Not tested'}</span>
                    </button>
                    {isWorking && (
                      <button
                        type="button"
                        className="apps-setup-action-reset"
                        onClick={() => handleResetAction(action)}
                      disabled={Boolean(busy) || Boolean(pendingTest) || Boolean(pathRecording)}
                        aria-label={`Retest ${ACTION_LABELS[action] || action}`}
                      >
                        Retest
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {pendingTest && (
              <div className="apps-setup-confirmation" role="dialog" aria-live="polite">
                <strong>{ACTION_PROMPTS[pendingTest.action] || `Did ${pendingTest.action.replace(/_/g, ' ')} work?`}</strong>
                <span>Automatic result: {pendingTest.outcome} ({pendingTest.reason.replace(/_/g, ' ')})</span>
                <div>
                  <button type="button" className="primary" onClick={() => handleConfirm('passed')} disabled={Boolean(busy)}>
                    Yes, it worked
                  </button>
                  <button type="button" onClick={() => handleConfirm('failed')} disabled={Boolean(busy)}>
                    No
                  </button>
                </div>
              </div>
            )}
            {passedCount > 0 && !pendingTest && (
              <div className="apps-setup-partial-actions">
                <p>
                  You do not need to test every control. Continue with the working controls now, or save
                  them without recording a playback baseline.
                </p>
                <div>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => baselineSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    disabled={Boolean(busy)}
                  >
                    Continue to Playback Baseline
                  </button>
                  <button type="button" onClick={handleComplete} disabled={Boolean(busy)}>
                    {busy === 'complete' ? 'Saving…' : 'Save Tested Controls Now'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="apps-configurator-section apps-configurator-section--prompt-detection">
            {workingActions.includes('enter') && (
              <>
              <PromptDetectionPanel
                session={session}
                packageName={packageName}
                appLabel={label}
                simple
                workingActions={workingActions}
                disabled={Boolean(busy) || Boolean(pendingTest) || Boolean(pathRecording)}
                onMessage={setMessage}
                onError={setError}
              />
              <details>
                <summary>Advanced detection and playback health</summary>
                <PlaybackHealthPanel
                  session={session}
                  appLabel={label}
                  disabled={Boolean(busy) || Boolean(pendingTest) || Boolean(pathRecording)}
                  onMessage={setMessage}
                  onError={setError}
                />
              </details>
              </>
            )}
          </div>

          <div className="apps-configurator-section">
            <details>
              <summary>Advanced launch recipe</summary>
              <LaunchRecipePanel
                session={session}
                appLabel={label}
                channelTargets={channelTargets}
                disabled={Boolean(busy) || Boolean(pendingTest) || Boolean(pathRecording)}
                onMessage={setMessage}
                onError={setError}
              />
            </details>
          </div>

          <div className="apps-configurator-section" ref={baselineSectionRef}>
            <h4>3. Teach known start points</h4>
            <p>
              First record a deterministic route from launch or resumed playback to a known home anchor.
              Then record shorter routes from that anchor to playback, Search, or any number of named
              destinations. If a profile picker,
              advert, sign-in prompt, or other interruption is visible, capture it separately so its screen
              signature and dismissal keys can be remembered.
            </p>
            {!pathRecording && (
              <div className="apps-setup-launch-sources">
                <strong>Choose a starting app</strong>
                <p>
                  Open another app first, then start recording. This tests a genuine app-to-app launch
                  instead of relaunching {label} over itself.
                </p>
                <div>
                  <button
                    type="button"
                    onClick={() => run('launch-source:suitestream', () => onLaunchApp('__suitestream__'))}
                    disabled={Boolean(busy)}
                  >
                    Suitestream
                  </button>
                  {launchSourceApps.map((sourceApp) => (
                    <button
                      key={sourceApp.package_name}
                      type="button"
                      onClick={() => run(
                        `launch-source:${sourceApp.package_name}`,
                        () => onLaunchApp(sourceApp.package_name)
                      )}
                      disabled={Boolean(busy)}
                    >
                      {sourceApp.app_name || sourceApp.package_name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!pathRecording ? (
              <div className="apps-setup-start-point-actions">
                <button
                  type="button"
                  className="primary"
                  onClick={() => handleStartPathRecording('anchor')}
                  disabled={Boolean(busy)}
                >
                  {busy === 'record:anchor'
                    ? `Launching ${label} and checking foreground…`
                    : `Launch ${label} and Record → Known Home`}
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => handleStartPathRecording('playback')}
                  disabled={Boolean(busy) || !savedStartPoints.some((entry) => entry.kind === 'anchor')}
                >
                  Record Known Home → First Playback
                </button>
                <button
                  type="button"
                  onClick={() => handleStartPathRecording('channel_list')}
                  disabled={Boolean(busy) || !savedStartPoints.some((entry) => entry.kind === 'anchor')}
                >
                  Record Known Home → Channel List Reference
                </button>
                <button
                  type="button"
                  onClick={() => handleStartPathRecording('search')}
                  disabled={Boolean(busy) || !savedStartPoints.some((entry) => entry.kind === 'anchor')}
                >
                  Record Known Home → Search
                </button>
                <div className="apps-named-destination-form">
                  <label>
                    <span>Named destination</span>
                    <input
                      type="text"
                      maxLength="100"
                      value={destinationName}
                      onChange={(event) => setDestinationName(event.target.value)}
                      placeholder="EPG or NZ Sky Sport 01"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleStartDestinationRecording}
                    disabled={
                      Boolean(busy)
                      || !destinationName.trim()
                      || !savedStartPoints.some((entry) => entry.kind === 'anchor')
                    }
                  >
                    Record Named Destination
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleStartPathRecording('interruption')}
                  disabled={!launchVerified || Boolean(busy)}
                >
                  Record Current One-Time Interruption
                </button>
                <button
                  type="button"
                  onClick={() => handleStartPathRecording('interruption', true)}
                  disabled={!launchVerified || Boolean(busy)}
                >
                  Capture Visual Interruption
                </button>
              </div>
            ) : (
              <div className="apps-setup-recorder" aria-live="polite">
                <strong>Recording: {pathRecording.label}</strong>
                <span>
                  {pathRecording.startEvidence.screenKey} start · {pathRecording.steps.length} recorded press(es)
                </span>
                {pathRecording.steps[0]?.sentAt && (
                  <span>
                    Initial wait: {Math.round(
                      Math.min(30000, pathRecording.steps[0].sentAt - pathRecording.startedAt) / 100
                    ) / 10}s
                  </span>
                )}
                {pathRecording.visualMode && latestScreenshot && (
                  <div className="apps-visual-template-training">
                    <p>
                      Drag a tight rectangle around stable artwork or text such as Skip. Avoid animated
                      video outside the control.
                    </p>
                    <div
                      className="apps-visual-template-selector"
                      onPointerDown={handleVisualPointerDown}
                      onPointerMove={handleVisualPointerMove}
                      onPointerUp={handleVisualPointerUp}
                      onPointerCancel={handleVisualPointerUp}
                      role="application"
                      aria-label="Draw a visual matching region"
                    >
                      <img
                        ref={visualImageRef}
                        src={latestScreenshot}
                        alt={`Visual interruption reference for ${label}`}
                        draggable="false"
                      />
                      {visualSelection && (
                        <span
                          className="apps-visual-template-selection"
                          style={{
                            left: `${visualSelection.x * 100}%`,
                            top: `${visualSelection.y * 100}%`,
                            width: `${visualSelection.width * 100}%`,
                            height: `${visualSelection.height * 100}%`,
                          }}
                        />
                      )}
                    </div>
                    <label className="apps-setup-run-policy">
                      <span>Match threshold: {Math.round(visualThreshold * 100)}%</span>
                      <input
                        type="range"
                        min="0.5"
                        max="0.99"
                        step="0.01"
                        value={visualThreshold}
                        onChange={(event) => setVisualThreshold(Number(event.target.value))}
                      />
                    </label>
                  </div>
                )}
                <div className="apps-recording-workspace">
                  <div className="apps-recording-remote">
                    <strong>Recording remote</strong>
                    <p>Only controls confirmed as working are available here. Every press is recorded.</p>
                    <div className="apps-recording-dpad">
                      {workingActions.includes('up') && (
                        <button type="button" className="up" onClick={() => handleNavigate('up')} disabled={Boolean(busy)}>
                          ↑
                        </button>
                      )}
                      {workingActions.includes('left') && (
                        <button type="button" className="left" onClick={() => handleNavigate('left')} disabled={Boolean(busy)}>
                          ←
                        </button>
                      )}
                      {workingActions.includes('enter') && (
                        <button type="button" className="enter" onClick={() => handleNavigate('enter')} disabled={Boolean(busy)}>
                          OK
                        </button>
                      )}
                      {workingActions.includes('right') && (
                        <button type="button" className="right" onClick={() => handleNavigate('right')} disabled={Boolean(busy)}>
                          →
                        </button>
                      )}
                      {workingActions.includes('down') && (
                        <button type="button" className="down" onClick={() => handleNavigate('down')} disabled={Boolean(busy)}>
                          ↓
                        </button>
                      )}
                    </div>
                    <div className="apps-recording-extra-controls">
                      {workingActions
                        .filter((action) => !['up', 'down', 'left', 'right', 'enter'].includes(action))
                        .map((action) => (
                          <button
                            key={action}
                            type="button"
                            onClick={() => handleNavigate(action)}
                            disabled={Boolean(busy)}
                          >
                            {ACTION_LABELS[action] || action.replace(/_/g, ' ')}
                          </button>
                        ))}
                    </div>
                  </div>
                  <aside className="apps-recording-timeline">
                    <strong>Recorded steps</strong>
                    {pathRecording.steps.length > 0 ? (
                      <ol>
                        {pathRecording.steps.map((step, index) => (
                          <li key={`${step.action}-${index}`}>
                            <span>{index + 1}. {ACTION_LABELS[step.action] || step.action}</span>
                            <small>{step.delayMs}ms</small>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p>Your recorded controls will appear here.</p>
                    )}
                  </aside>
                </div>
                {pathRecording.kind === 'interruption' && (
                  <label className="apps-setup-run-policy">
                    <span>Use this response</span>
                    <select
                      value={pathRecording.runPolicy}
                      onChange={(event) => setPathRecording((current) => ({
                        ...current,
                        runPolicy: event.target.value,
                        label: event.target.value === 'whenever_matched'
                          ? 'Recurring screen interruption'
                          : 'One-time interruption',
                      }))}
                    >
                      <option value="once_per_install">Once per app installation</option>
                      <option value="whenever_matched">Whenever this screen appears</option>
                    </select>
                  </label>
                )}
                <div>
                  <button
                    type="button"
                    className="primary"
                    onClick={handleSavePathRecording}
                    disabled={Boolean(busy) || pathRecording.steps.length === 0}
                  >
                    {busy === 'save-path' ? 'Saving…' : 'Stop and Save Navigation'}
                  </button>
                  <button type="button" onClick={handleCancelPathRecording} disabled={Boolean(busy)}>
                    Cancel Recording
                  </button>
                </div>
              </div>
            )}
            {error && !pathRecording && (
              <div className="apps-configurator-status error">
                Recording did not start: {error}
              </div>
            )}
            {baseline && (
              <div className="apps-setup-evidence">
                <strong>Playback baseline recorded</strong>
                <span>{baseline.expectedScreenKey} · {baseline.expectedClassName || 'class unavailable'}</span>
              </div>
            )}
            {savedStartPoints.length > 0 && (
              <div className="apps-setup-saved-start-points">
                {savedStartPoints.map((startPoint) => (
                  <div key={startPoint.id}>
                    <span className="apps-setup-saved-start-point-detail">
                      <strong>{startPoint.label}</strong>
                      <span>
                        {startPoint.steps.length} step(s) · starts at {startPoint.startScreen.screenKey}
                        {startPoint.kind === 'interruption'
                          ? ` · ${startPoint.runPolicy === 'whenever_matched' ? 'whenever matched' : 'once per install'}`
                          : ''}
                      </span>
                    </span>
                    <div className="apps-saved-start-point-actions">
                      <button
                        type="button"
                        onClick={() => handleTestStartPoint(startPoint)}
                        disabled={Boolean(busy)}
                      >
                        {busy === `test-path:${startPoint.id}`
                          ? 'Testing…'
                          : startPoint.kind === 'interruption'
                            ? 'Test Response'
                            : 'Launch and Test'}
                      </button>
                      {startPoint.kind !== 'interruption' && (
                        <button
                          type="button"
                          onClick={() => handleOpenStartPointEditor(startPoint)}
                          disabled={Boolean(busy)}
                        >
                          Edit
                        </button>
                      )}
                      {startPoint.kind === 'destination' && (
                        findNavigationDestinationTarget(channelTargets, startPoint.id) ? (
                          <button
                            type="button"
                            onClick={() => handleTestChannelTarget(
                              findNavigationDestinationTarget(channelTargets, startPoint.id)
                            )}
                            disabled={Boolean(busy)}
                          >
                            {busy === `test-target:${
                              findNavigationDestinationTarget(channelTargets, startPoint.id)?.id
                            }` ? 'Opening…' : 'Open Destination'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handlePublishDestination(startPoint)}
                            disabled={Boolean(busy)}
                          >
                            {busy === `publish-destination:${startPoint.id}`
                              ? 'Publishing…'
                              : 'Publish Destination'}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {startPointEditor && (
              <div className="apps-start-point-editor" role="dialog" aria-label={`Edit ${startPointEditor.label}`}>
                <div className="apps-start-point-editor-header">
                  <div>
                    <span className="apps-setup-eyebrow">Macro editor</span>
                    <h5>{startPointEditor.label}</h5>
                    <p>
                      Adjust automatic timing, or send one control at a time while watching the TV.
                    </p>
                  </div>
                  <button type="button" onClick={() => setStartPointEditor(null)} disabled={Boolean(busy)}>
                    Close
                  </button>
                </div>
                <div className="apps-start-point-editor-grid">
                  <div className="apps-start-point-editor-steps">
                    <label>
                      <span>Wait before first control</span>
                      <span className="apps-timing-input">
                        <input
                          type="number"
                          min="0"
                          max="30000"
                          step="100"
                          value={startPointEditor.initialDelayMs}
                          onChange={(event) => setStartPointEditor((current) => ({
                            ...current,
                            initialDelayMs: Number(event.target.value),
                          }))}
                        />
                        ms
                      </span>
                    </label>
                    {startPointEditor.steps.map((step, index) => (
                      <div
                        key={`${step.action}-${index}`}
                        className={
                          startPointEditor.currentStepIndex === index
                            ? 'apps-start-point-editor-step current'
                            : 'apps-start-point-editor-step'
                        }
                      >
                        <button
                          type="button"
                          className="apps-start-point-editor-step-select"
                          onClick={() => setStartPointEditor((current) => ({
                            ...current,
                            currentStepIndex: index,
                          }))}
                        >
                          <span>{index + 1}</span>
                          <strong>{ACTION_LABELS[step.action] || step.action}</strong>
                        </button>
                        <label>
                          <span>Wait after</span>
                          <span className="apps-timing-input">
                            <input
                              type="number"
                              min="0"
                              max="10000"
                              step="50"
                              value={step.delayMs}
                              onChange={(event) => handleUpdateEditorStep(index, {
                                delayMs: Number(event.target.value),
                              })}
                            />
                            ms
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="apps-start-point-walkthrough">
                    <strong>Manual walkthrough</strong>
                    <p>
                      Reboot or reopen the app, choose any step as the starting point, then send controls
                      individually. Saved timing is ignored in this mode.
                    </p>
                    <div className="apps-walkthrough-status">
                      {startPointEditor.currentStepIndex < startPointEditor.steps.length
                        ? `Next: ${startPointEditor.currentStepIndex + 1}. ${
                            ACTION_LABELS[
                              startPointEditor.steps[startPointEditor.currentStepIndex].action
                            ] || startPointEditor.steps[startPointEditor.currentStepIndex].action
                          }`
                        : 'Sequence complete'}
                    </div>
                    <div className="apps-walkthrough-actions">
                      <button
                        type="button"
                        onClick={() => run('edit-path:launch', () => onLaunchApp(packageName))}
                        disabled={Boolean(busy)}
                      >
                        {busy === 'edit-path:launch' ? 'Launching…' : `Launch ${label}`}
                      </button>
                      <button
                        type="button"
                        className="primary"
                        onClick={handleSendManualStep}
                        disabled={
                          Boolean(busy)
                          || startPointEditor.currentStepIndex >= startPointEditor.steps.length
                        }
                      >
                        {busy === 'edit-path:step' ? 'Sending…' : 'Send next step'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setStartPointEditor((current) => ({
                          ...current,
                          currentStepIndex: 0,
                        }))}
                        disabled={Boolean(busy)}
                      >
                        Restart at step 1
                      </button>
                      <button
                        type="button"
                        onClick={handleCaptureEditorScreen}
                        disabled={Boolean(busy)}
                      >
                        {busy === 'edit-path:capture' || busy === 'edit-path:screenshot'
                          ? 'Capturing…'
                          : 'Capture current screen'}
                      </button>
                    </div>
                    {startPointEditor.screenshot && (
                      <div className="apps-walkthrough-capture">
                        <img src={startPointEditor.screenshot} alt="Current TV screen capture" />
                        <button
                          type="button"
                          className="primary"
                          onClick={handleTrainCapturedInterruption}
                        >
                          Train a response for this screen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="apps-start-point-editor-footer">
                  <button
                    type="button"
                    className="primary"
                    onClick={handleSaveStartPointEditor}
                    disabled={Boolean(busy)}
                  >
                    {busy === 'edit-path:save' ? 'Saving…' : 'Save timing changes'}
                  </button>
                  <span>Manual walkthrough does not modify the saved macro.</span>
                </div>
              </div>
            )}
            <div className="apps-setup-search-text-test">
              <label>
                <span>Search text test</span>
                <input
                  type="text"
                  value={searchTestText}
                  onChange={(event) => setSearchTestText(event.target.value)}
                  placeholder="Test channel name"
                />
              </label>
              <button type="button" disabled title="Requires Android bridge text-entry support">
                Send Text to Focused Search Field
              </button>
              <p>
                Search focus can be recorded now. Direct text entry is not yet exposed by the installed
                Android bridge, so this test will become available after that protocol capability is added.
              </p>
            </div>
          </div>

          <div className="apps-configurator-section">
            <h4>4. Save favourite channels</h4>
            <p>
              Direct channel numbers are tried first when supplied. A channel-list fallback starts from
              the recorded reference and automatically chooses the shorter route when the list wraps.
            </p>
            <div className="apps-channel-target-form">
              <label>
                <span>Favourite name</span>
                <input
                  type="text"
                  value={channelForm.name}
                  onChange={(event) => setChannelForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))}
                  placeholder="BBC News"
                />
              </label>
              <label>
                <span>Direct channel number (optional)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={channelForm.channelNumber}
                  onChange={(event) => setChannelForm((current) => ({
                    ...current,
                    channelNumber: event.target.value.replace(/\D/g, '').slice(0, 4),
                  }))}
                  placeholder="23"
                />
              </label>
              <fieldset disabled={!savedStartPoints.some((entry) => entry.kind === 'channel_list')}>
                <legend>Channel-list fallback</legend>
                <label>
                  <span>Reference position</span>
                  <input
                    type="number"
                    min="1"
                    value={channelForm.currentPosition}
                    onChange={(event) => setChannelForm((current) => ({
                      ...current,
                      currentPosition: event.target.value,
                    }))}
                  />
                </label>
                <label>
                  <span>Favourite position</span>
                  <input
                    type="number"
                    min="1"
                    value={channelForm.targetPosition}
                    onChange={(event) => setChannelForm((current) => ({
                      ...current,
                      targetPosition: event.target.value,
                    }))}
                  />
                </label>
                <label>
                  <span>Total channels</span>
                  <input
                    type="number"
                    min="2"
                    max="500"
                    value={channelForm.totalChannels}
                    onChange={(event) => setChannelForm((current) => ({
                      ...current,
                      totalChannels: event.target.value,
                    }))}
                    placeholder="32"
                  />
                </label>
                <label className="apps-channel-wrap-toggle">
                  <input
                    type="checkbox"
                    checked={channelForm.wraps}
                    onChange={(event) => setChannelForm((current) => ({
                      ...current,
                      wraps: event.target.checked,
                    }))}
                  />
                  The list wraps at its boundaries
                </label>
              </fieldset>
              {!savedStartPoints.some((entry) => entry.kind === 'channel_list') && (
                <p>
                  Record Known Home → Channel List Reference above to enable positional navigation.
                </p>
              )}
              <button
                type="button"
                className="primary"
                onClick={handleCreateChannelTarget}
                disabled={Boolean(busy) || !savedStartPoints.some((entry) => entry.kind === 'anchor')}
              >
                {busy === 'create-channel' ? 'Saving favourite…' : 'Save Favourite Channel'}
              </button>
            </div>
            {channelTargets.some((target) => target.kind === 'channel') && (
              <div className="apps-channel-target-list">
                {channelTargets.filter((target) => target.kind === 'channel').map((target) => (
                  <div key={target.id}>
                    <span>
                      <strong>{target.name}</strong>
                      <small>
                        {target.strategies.map((strategy) =>
                          strategy.type === 'channel_number' ? 'direct number' : 'channel list'
                        ).join(' → ')}
                      </small>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleTestChannelTarget(target)}
                      disabled={Boolean(busy)}
                    >
                      {busy === `test-target:${target.id}` ? 'Testing…' : 'Test Now'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="apps-configurator-section">
            <label className="apps-configurator-toggle">
              <input
                type="checkbox"
                checked={includeScreenshots}
                onChange={(event) => setIncludeScreenshots(event.target.checked)}
              />
              Include private screenshot previews in new evidence captures
            </label>
            <p>Screenshots are optional, size-capped, and retained only in the latest evidence records.</p>
            {latestScreenshot && (
              <div className="apps-configurator-snapshot-image-frame">
                <img src={latestScreenshot} alt={`Latest setup evidence for ${label}`} className="apps-configurator-snapshot-image" />
              </div>
            )}
          </div>

          <div className="apps-configurator-section apps-setup-save">
            <h4>5. Save tested setup</h4>
            <p>
              Save publishes confirmed controls and the baseline together. You can save a partial setup and
              retrain it later.
            </p>
            <button
              type="button"
              className="primary"
              onClick={handleComplete}
              disabled={Boolean(busy) || (!passedCount && !baseline)}
            >
              {busy === 'complete' ? 'Saving…' : 'Save Setup'}
            </button>
          </div>
        </>
      )}

      {message && <div className="apps-configurator-status">{message}</div>}
      {error && <div className="apps-configurator-status error">{error}</div>}
    </section>
  );
}

export default AppTrainingConfigurator;
